import { GetStaticProps, InferGetStaticPropsType, NextPage } from "next";
import {
  AnalyticsContentProperties,
  SetAnalyticsDataForContent,
  apiV1ToAnalyticsContentProperties,
} from "~/components/Analytics";
import {
  ArticlePage,
  ArticlePageProps,
} from "~/components/ArticlePage/ArticlePage";
import { apiToAssetDownloadV1Props } from "~/components/AssetDownload";
import { postsToContentPromoProps } from "~/components/ContentPromo/postToContentPromoProps";
import { apiChartToEvervizChartProps } from "~/components/EvervizChart/apiChartToEvervizChartProps";
import { apiDatavizToEvervizChartProps } from "~/components/EvervizChart/apiDatavizToEvervizChartProps";
import {
  PageMeta,
  PostMetadata,
  apiPostToPageMetadata,
} from "~/components/PageMeta";
import { apiToPostEditCTAProps } from "~/components/PostEditCTA/apiToPostEditCTAProps";
import {
  fetchInstantIntel,
  fetchInstantIntelPreview,
} from "~/lib/server/asgardApiCalls";
import { createGetStaticPathsForPosts } from "~/lib/server/createGetStaticPathsForPosts";
import { fetchFeatureFlags } from "~/lib/server/featureFlags";
import { postToArticlePageProps } from "~/lib/server/postToArticlePageProps";
import { wrapGetStaticProps } from "~/lib/server/wrapGetProps";
import { PostTypeEnum } from "~/types/asgard/PostType";

export type InstantIntelPageParams = {
  instantIntelParam: string;
};

type InstantIntelPagePropsWithMeta = ArticlePageProps & {
  meta: PostMetadata;
  analytics: AnalyticsContentProperties;
};

export const getStaticPaths = createGetStaticPathsForPosts(
  PostTypeEnum.InstantIntel,
  PostTypeEnum.InstantIntel,
  "slug"
);

export const getStaticProps: GetStaticProps<
  InstantIntelPagePropsWithMeta,
  InstantIntelPageParams
> = wrapGetStaticProps(async ({ params, preview }) => {
  const { instantIntelParam } = params!;
  const fetchMethod = preview ? fetchInstantIntelPreview : fetchInstantIntel;
  const { data: post } = await fetchMethod(instantIntelParam);
  const postEditCTAProps = await apiToPostEditCTAProps({
    isPreview: preview,
    postId: post.id,
    postParam: instantIntelParam,
  });

  const chartPostData = post.acf.chart_post
    ? apiChartToEvervizChartProps({
        chart: post.acf.chart_post,
      })
    : null;

  const inlineChartData = post.acf.inline_chart
    ? apiDatavizToEvervizChartProps(post.acf.inline_chart)
    : null;

  const { enableSidecar } = await fetchFeatureFlags();

  return {
    props: {
      ...postToArticlePageProps({
        enableSidecar: preview || enableSidecar,
        post,
        preview,
      }),
      postEditCTAProps,
      // explicitly remove the featured image from instant intel
      articleFeaturedImageProps: null,
      assetDownloadProps: apiToAssetDownloadV1Props({
        downloadableAssets: post.acf.downloadable_assets,
        postId: post.id,
        postName: post.title.rendered,
        assetSummary: post.acf.asset_summary,
      }),
      headerChartProps: inlineChartData || chartPostData,
      meta: apiPostToPageMetadata(post),
      contentPromoProps: postsToContentPromoProps(post.relatedStories),
      analytics: apiV1ToAnalyticsContentProperties(post),
    } satisfies InstantIntelPagePropsWithMeta,
  };
});

const InstantIntelPage: NextPage<
  InferGetStaticPropsType<typeof getStaticProps>
> = ({ analytics, meta, ...props }) => (
  <>
    <SetAnalyticsDataForContent {...analytics} />
    <PageMeta {...meta} />
    <ArticlePage {...props} />
  </>
);

export default InstantIntelPage;
